import PropTypes from 'prop-types';
import React, { useMemo, memo } from 'react';
import { useWtrSelector } from 'redux/hooks';
import Typography from '@johnlewispartnership/wtr-ingredients/foundations/typography';
import Spinner from '@johnlewispartnership/wtr-ingredients/ingredients/Spinner';

import getIsLoadingSubs from 'redux/modules/trolley/selectors/get-loading-subs';
import { getSubProductsByLineNumber } from 'redux/modules/trolley/selectors/get-item-sub-products';

import SubProductPod from '../SubProductPod';

import styles from './index.scss';

const ProductsList = memo(({ emptySection, sortedArray, productPodAddText, listDefinitions }) => {
  if (sortedArray.length > 0) {
    return (
      <ul className={styles.productsWrapper}>
        {sortedArray.map(({ lineNumber }) => {
          return (
            <SubProductPod
              key={lineNumber}
              lineNumber={lineNumber}
              productPodAddText={productPodAddText}
              listDefinitions={listDefinitions}
            />
          );
        })}
      </ul>
    );
  }

  return <div className={styles.emptySection}>{emptySection}</div>;
});

ProductsList.displayName = 'ProductsList';

ProductsList.propTypes = {
  emptySection: PropTypes.node,
  productPodAddText: PropTypes.string,
  sortedArray: PropTypes.arrayOf(
    PropTypes.shape({
      lineNumber: PropTypes.string,
    }),
  ),
  listDefinitions: PropTypes.shape({
    curation: PropTypes.string,
    format: PropTypes.string,
    page: PropTypes.string,
    title: PropTypes.string,
  }),
};

const subsProductListDefaultPropEmptySection = (
  <Typography styleAs="sectionHeading" textStyle="heading-xl">
    Unfortunately we have no alternatives to show you
  </Typography>
);

const SubsProductList = ({
  emptySection = subsProductListDefaultPropEmptySection,
  headingText,
  lineNumber: subProductLineNumber,
  maxProducts = 10,
  productPodAddText = 'Select',
  listDefinitions,
}) => {
  const substitutionProducts = useWtrSelector(state =>
    getSubProductsByLineNumber(state, subProductLineNumber),
  );
  const isLoadingSubs = useWtrSelector(getIsLoadingSubs);

  const sortedArray = useMemo(() => {
    return substitutionProducts
      .slice(0, maxProducts)
      .sort((itemA, itemB) => (itemA.score < itemB.score ? 1 : -1));
  }, [substitutionProducts, maxProducts]);

  return (
    <section>
      {headingText && (
        <header className={styles.header}>
          <Typography element="h2" styleAs="paragraphHeading" textStyle="heading-l">
            {headingText}
          </Typography>
        </header>
      )}
      {isLoadingSubs ? (
        <div className={styles.spinnerWrapper}>
          <Spinner size="large" isActive />
        </div>
      ) : (
        <ProductsList
          emptySection={emptySection}
          productPodAddText={productPodAddText}
          sortedArray={sortedArray}
          listDefinitions={listDefinitions}
        />
      )}
    </section>
  );
};

SubsProductList.displayName = 'SubsProductList';

SubsProductList.propTypes = {
  emptySection: PropTypes.node,
  headingText: PropTypes.string,
  lineNumber: PropTypes.string.isRequired,
  maxProducts: PropTypes.number,
  productPodAddText: PropTypes.string,
  listDefinitions: PropTypes.shape({
    curation: PropTypes.string,
    format: PropTypes.string,
    page: PropTypes.string,
    title: PropTypes.string,
  }),
};

export default memo(SubsProductList);
